import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { setDocTitle } from '../../utils/utilities';
import { useAppBar } from '../../context/AppBarContext';
import songService from '../../services/songService';
import PageBar from '../../components/PageBar';
import SearchBox from '../../components/common/SearchBox';
import NoContent from '../../components/common/NoContent';
import SearchSongsResult from './components/PublicSongsResult';
import SearchSongsNotFound from './components/PublicSongsNotFound';
import Icon from '../../components/common/Icon';
import { ReactComponent as MusicIcon } from '../../icons/music-double-note.svg';

import './PublicSongs.scss';

const SearchSongs = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { traySlot } = useAppBar();
  const [songs, setSongs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    setDocTitle(t('nav.publicSongs'));
  }, [t]);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q) {
      setSearchQuery(q);
      runSearch(q);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reads ?q= on mount only; runSearch rewrites it
  }, []);

  const runSearch = async (query) => {
    setSongs([]);
    setNoResults(false);

    if (query.length <= 3) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    try {
      const { data: results } = await songService.getSearchSongs(query, abortRef.current.signal);

      if (results.length === 0) setNoResults(true);
      setSongs(results);
    } catch (ex) {
      if (ex.name !== 'CanceledError') throw ex;
    }
    setLoading(false);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    history.replace({ search: query ? `?q=${encodeURIComponent(query)}` : '' });
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 300);
  };

  const handleClearInput = () => {
    abortRef.current?.abort();
    clearTimeout(debounceRef.current);
    setSearchQuery('');
    setSongs([]);
    setNoResults(false);
    setLoading(false);
    history.replace({ search: '' });
  };

  // In the tray under the app bar, so it lines up with the bar and stays stuck to it over the results
  const searchBox = (
    <SearchBox
      autoFocus
      value={searchQuery}
      label={t('publicSongs.search')}
      onChange={handleSearch}
      onClearInput={handleClearInput}
      bar
      count={
        songs.length > 0 && (
          <span aria-label={t('publicSongs.found', { count: songs.length })}>
            <Icon icon={<MusicIcon />} />
            {songs.length}
          </span>
        )
      }
    />
  );

  return (
    <div className="PublicSongs mg-container">
      <PageBar back={{ label: t('common.back'), icon: true }} title={t('nav.publicSongs')} />
      {traySlot && createPortal(searchBox, traySlot)}
      <div className="content">
        <NoContent loading={loading} />
        {!loading && noResults && <SearchSongsNotFound searchQuery={searchQuery} />}
        {!loading && songs.length > 0 && <SearchSongsResult songs={songs} />}
      </div>
    </div>
  );
};

export default SearchSongs;
